/* global Office, window */

/**
 * Typed client for the per-user FactSet OAuth endpoints that the add-in
 * server exposes under `/api/auth/factset/oauth`.
 *
 * Two surfaces live here:
 *
 *  1. **Low-level wire calls** (`getFactSetOAuthSession`,
 *     `getFactSetOAuthAuthorizationUrl`, `exchangeFactSetOAuthCode`) are
 *     re-exported from the shared FactSet OAuth client so Excel and
 *     PowerPoint can probe session state without crossing host folders.
 *
 *  2. **Connect orchestration** (`connectFactSetOAuth`,
 *     `ensureFactSetOAuthConnected`) — Office-dialog (or popup-fallback)
 *     driven flows that the post-WorkOS-login hook in `hooks/useAuth.ts`
 *     uses to auto-wire the FactSet grant once per session. The dialog
 *     opener mirrors `services/authService.ts::openLoginDialogOffice`
 *     so it works inside the FactSet Workstation webview (which blocks
 *     `window.open` for cross-origin popups but allows Office dialogs).
 *
 * All route paths come from `factsetConstants.ts` (the typed mirror of
 * `apps/shared/factsetConstants.js`) so a server-side rename of a mount
 * path is a one-file change instead of a manual search-and-replace.
 */

import {
  disconnectFactSetOAuthGrant,
  exchangeFactSetOAuthCode,
  getFactSetOAuthAuthorizationUrl,
  getFactSetOAuthSession,
  type FactSetOAuthErrorPayload,
  type FactSetOAuthSessionData,
} from "../../../shared/factset/factsetOAuthClient";
import { clearFactSetClientSessionState } from "../../../shared/factset/factsetSessionLifecycle";

export {
  exchangeFactSetOAuthCode,
  getFactSetOAuthAuthorizationUrl,
  getFactSetOAuthSession,
  type FactSetOAuthErrorPayload,
  type FactSetOAuthSessionData,
};

/**
 * Clears the user's FactSet OAuth grant from the sealed session.
 * Idempotent on the server (it returns 204 even when there's nothing to
 * clear); we still surface a typed error for transport-level failures
 * to the caller.
 *
 * After a successful disconnect we also clear client-side state derived
 * from the FactSet grant. Feature modules register their own handlers
 * through `factsetSessionLifecycle`, so this OAuth service does not need
 * to know about Excel caches or other host-specific state.
 */
export async function disconnectFactSetOAuth(): Promise<void> {
  await disconnectFactSetOAuthGrant();
  clearFactSetClientSessionState();
}

// ---------------------------------------------------------------------------
// Connect orchestration (Office dialog + popup fallback)
// ---------------------------------------------------------------------------

/**
 * Shape of the JSON payload that the FactSet OAuth callback relay
 * (`apps/server/utils/callbackResponse.js`) posts back into the parent
 * taskpane via `Office.context.ui.messageParent` or
 * `window.opener.postMessage`. Mirrors `DialogAuthResult` in
 * `services/authService.ts` — kept local so the two flows can evolve
 * independently.
 */
interface FactSetDialogResult {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

function hasOfficeDialog(): boolean {
  try {
    return (
      typeof Office !== "undefined" &&
      !!Office.context?.ui?.displayDialogAsync
    );
  } catch {
    return false;
  }
}

/**
 * Open the FactSet OAuth authorize URL inside an Office dialog and
 * resolve with the relayed code+state payload. Mirrors
 * `openLoginDialogOffice` in `services/authService.ts` — same channel,
 * same parse behaviour, same cancellation error code (12006). The
 * relay HTML (`apps/server/utils/callbackResponse.js`) is shared
 * between the WorkOS callback and the FactSet OAuth callback, so the
 * receive side is byte-identical.
 */
function openFactSetOAuthDialogOffice(
  authorizationUrl: string
): Promise<FactSetDialogResult> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authorizationUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }

        const dialog = result.value;
        let settled = false;

        const settleResolve = (message: FactSetDialogResult): void => {
          if (settled) return;
          settled = true;
          try {
            dialog.close();
          } catch {
            // Ignore close errors
          }
          resolve(message);
        };

        const settleReject = (error: Error): void => {
          if (settled) return;
          settled = true;
          try {
            dialog.close();
          } catch {
            // Ignore close errors
          }
          reject(error);
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          try {
            const message = JSON.parse(
              (arg as { message: string }).message
            ) as FactSetDialogResult;
            if (message.error) {
              settleReject(
                new Error(message.error_description || message.error)
              );
            } else {
              settleResolve(message);
            }
          } catch {
            settleReject(new Error("Failed to parse FactSet auth response"));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          const eventArg = arg as { error: number };
          if (eventArg.error === 12006) {
            settleReject(new Error("FactSet connection cancelled"));
          } else {
            settleReject(
              new Error(`FactSet OAuth dialog error (${eventArg.error})`)
            );
          }
        });
      }
    );
  });
}

/**
 * Browser-popup fallback for `connectFactSetOAuth`. Used when running
 * outside an Office host (local browser dev, automated tests). Mirrors
 * `openLoginDialogPopup` in `services/authService.ts`.
 *
 * Note: this path will be blocked inside the FactSet Workstation webview
 * (cross-origin `window.open` is denied by the embedded browser); that's
 * fine because in that environment `hasOfficeDialog()` returns true and
 * the Office dialog path is used instead.
 */
function openFactSetOAuthDialogPopup(
  authorizationUrl: string
): Promise<FactSetDialogResult> {
  return new Promise((resolve, reject) => {
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      authorizationUrl,
      "finster_factset_auth",
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );

    if (!popup) {
      reject(new Error("Popup blocked — please allow popups for this site."));
      return;
    }

    const cleanup = (): void => {
      window.removeEventListener("message", onMessage);
      clearInterval(pollTimer);
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;

      let data: FactSetDialogResult;
      try {
        data =
          typeof event.data === "string"
            ? (JSON.parse(event.data) as FactSetDialogResult)
            : (event.data as FactSetDialogResult);
      } catch {
        return;
      }

      if (!data || (!data.code && !data.error)) return;

      cleanup();
      try {
        popup.close();
      } catch {
        // already closed
      }

      if (data.error) {
        reject(new Error(data.error_description || data.error));
      } else {
        resolve(data);
      }
    };

    window.addEventListener("message", onMessage);

    const pollTimer = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("FactSet connection cancelled"));
      }
    }, 500);
  });
}

/**
 * End-to-end "connect FactSet" flow: ask the server for a fresh
 * authorization URL (PKCE/state are generated and persisted server-
 * side), drive the user through the OAuth consent screen via the
 * Office dialog (or popup fallback), and exchange the returned code
 * for tokens that the server persists into the sealed session.
 *
 * Throws on any failure (user cancellation, network, exchange rejection).
 * Callers that need fail-silently semantics should use
 * `ensureFactSetOAuthConnected()` instead.
 */
export async function connectFactSetOAuth(): Promise<void> {
  const authorizationUrl = await getFactSetOAuthAuthorizationUrl();
  const result = hasOfficeDialog()
    ? await openFactSetOAuthDialogOffice(authorizationUrl)
    : await openFactSetOAuthDialogPopup(authorizationUrl);
  if (!result.code || !result.state) {
    throw new Error("FactSet OAuth callback did not include code/state.");
  }
  await exchangeFactSetOAuthCode(result.code, result.state);
}

/**
 * Single-flight forced reconnect. Used when an API call discovers the
 * stored token is invalid (e.g., refresh rejected by FactSet). All
 * concurrent callers share the same in-flight dialog — no duplicate popups.
 */
let inflightReconnect: Promise<void> | null = null;

export function forceReconnectFactSetOAuth(): Promise<void> {
  if (!inflightReconnect) {
    inflightReconnect = connectFactSetOAuth().finally(() => {
      inflightReconnect = null;
    });
  }
  return inflightReconnect;
}

/**
 * sessionStorage key used to remember that the user dismissed or
 * failed the auto-connect attempt in the current browser session.
 * Cleared on a successful connect so a subsequent disconnect→reconnect
 * cycle is unaffected. Scoped to sessionStorage (not localStorage) so
 * a fresh window always gets one more chance.
 */
const AUTO_CONNECT_DISMISSED_KEY = "finster_factset_auto_connect_dismissed";

function rememberDismissal(): void {
  try {
    sessionStorage.setItem(AUTO_CONNECT_DISMISSED_KEY, "1");
  } catch {
    // sessionStorage may be unavailable (privacy mode / sandboxed iframe).
  }
}

function wasDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(AUTO_CONNECT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function clearDismissal(): void {
  try {
    sessionStorage.removeItem(AUTO_CONNECT_DISMISSED_KEY);
  } catch {
    // sessionStorage may be unavailable; ignore.
  }
}

/**
 * Outcome of `ensureFactSetOAuthConnected()`. Stable strings so callers
 * (and telemetry / logs) can branch without sniffing message text.
 */
export type FactSetAutoConnectOutcome =
  | "already_connected"
  | "connected"
  | "dismissed"
  | "unavailable"
  | "failed";

export interface FactSetAutoConnectResult {
  outcome: FactSetAutoConnectOutcome;
  hasTextToFormulaAccess: boolean;
  error?: string;
}

let inflightEnsure: Promise<FactSetAutoConnectResult> | null = null;

/**
 * Idempotent "make sure the user is connected to FactSet OAuth"
 * helper, suitable for calling on every authenticated session. The
 * post-WorkOS-login hook in `hooks/useAuth.ts` calls this once per
 * authenticated session so the FactSet TTF gate
 * (`excel/excelService.ts::computeTtfRunContext`) flips to open
 * without any explicit user action.
 *
 * Behaviour:
 *  - Single-flight via a module-level promise so concurrent React
 *    effects share one in-flight attempt.
 *  - Probes `getFactSetOAuthSession()` first; if `connected` AND the
 *    server reports valid Text-to-Formula access, returns `already_connected` without
 *    opening a dialog.
 *  - Otherwise opens the connect dialog (Office or popup fallback).
 *    On success: clears the dismissal flag and returns `connected`.
 *    On user cancellation: stores a sessionStorage dismissal flag
 *    so subsequent calls in the same session no-op as `dismissed`
 *    (no Office dialog spam after the user said "no").
 *  - Server probe failures collapse to `unavailable` (no popup).
 *    Connect failures collapse to `failed` and also set the
 *    dismissal flag so we don't loop on a server bug or invalid
 *    config.
 *
 * Never throws — TTF fall-back is the contract, so the caller can
 * always render its happy path without try/catch boilerplate.
 */
export async function ensureFactSetOAuthConnected(): Promise<FactSetAutoConnectResult> {
  if (inflightEnsure) return inflightEnsure;

  inflightEnsure = (async (): Promise<FactSetAutoConnectResult> => {
    let session;
    try {
      session = await getFactSetOAuthSession();
    } catch (error) {
      return {
        outcome: "unavailable",
        hasTextToFormulaAccess: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }

    const hasTtf =
      session.connected === true &&
      session.hasTextToFormulaAccess === true;

    if (hasTtf) {
      clearDismissal();
      return {
        outcome: "already_connected",
        hasTextToFormulaAccess: true,
      };
    }

    if (wasDismissedThisSession()) {
      return {
        outcome: "dismissed",
        hasTextToFormulaAccess: false,
      };
    }

    try {
      await connectFactSetOAuth();
    } catch (error) {
      rememberDismissal();
      const message = error instanceof Error ? error.message : String(error);
      const userCancelled = /cancelled|dialog\s+.*already\s+open/i.test(message);
      return {
        outcome: userCancelled ? "dismissed" : "failed",
        hasTextToFormulaAccess: false,
        error: message,
      };
    }

    let refreshed;
    try {
      refreshed = await getFactSetOAuthSession();
    } catch (error) {
      return {
        outcome: "failed",
        hasTextToFormulaAccess: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }

    const refreshedHasTtf =
      refreshed.connected === true &&
      refreshed.hasTextToFormulaAccess === true;

    if (!refreshedHasTtf) {
      // Token was stored but TTF scope wasn't granted — surface as
      // failed so the caller can decide whether to retry next session.
      // We don't dismiss in this case: the user already paid the
      // OAuth UX cost; if they reload the page the consent screen
      // may show again with the missing scope this time.
      return {
        outcome: "failed",
        hasTextToFormulaAccess: false,
        error: "FactSet OAuth completed but the TTF scope was not granted.",
      };
    }

    clearDismissal();
    return {
      outcome: "connected",
      hasTextToFormulaAccess: true,
    };
  })();

  try {
    return await inflightEnsure;
  } finally {
    inflightEnsure = null;
  }
}

